/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { API } from 'middleware/api';

export const FEMALE_MODEL = 'FEMALE_MODEL';
export const HAIR = 'HAIR';
export const INFLUENCER = 'INFLUENCER';
export const MAKEUP = 'MAKEUP';
export const MALE_MODEL = 'MALE_MODEL';
export const PHOTOGRAPHER = 'PHOTOGRAPHER';
export const STYLIST = 'STYLIST';

export const SKILL_TYPES = [
  FEMALE_MODEL,
  HAIR,
  INFLUENCER,
  MAKEUP,
  MALE_MODEL,
  PHOTOGRAPHER,
  STYLIST,
];

export const NEW_JOB = 'NEW_JOB';

export const TALENT_MAKES_OFFER = 'TALENT_MAKES_OFFER';
export const CLIENT_MAKES_OFFER = 'CLIENT_MAKES_OFFER';

export const CREATE_JOB_REQUEST = 'CREATE_JOB_REQUEST';
export const CREATE_JOB_SUCCESS = 'CREATE_JOB_SUCCESS';
export const CREATE_JOB_FAILURE = 'CREATE_JOB_FAILURE';

/**
 * Create action object that is used to create a new job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {Object} job - The job object.
 * @param {string} job.job_type - What kind of job is it?
 * @param {string} job.job_visibility - Who is allowed to see this job.
 * @param {string} job.picture_cloudinary_public_id - Cloudinary public ID of a picture representing this job.
 * @param {string} job.title - A short title for this job.
 * @param {string} job.description - Description of this job.
 * @param {string} job.city - The city.
 * @param {string} job.country - Country Code.
 * @param {date} job.date_starts - The date at which this job will start. YYYY-MM-DD
 * @param {date} job.date_ends - The date at which this job will end. YYYY-MM-DD
 *
 * @return {Object}
 */
export const createJob = (token, job) => ({
  [API]: {
    endpoint: '/job/create',
    method: 'POST',
    body: job,
    token,
    types: [CREATE_JOB_REQUEST, CREATE_JOB_SUCCESS, CREATE_JOB_FAILURE],
  },
});

export const UPDATE_JOB_REQUEST = 'UPDATE_JOB_REQUEST';
export const UPDATE_JOB_SUCCESS = 'UPDATE_JOB_SUCCESS';
export const UPDATE_JOB_FAILURE = 'UPDATE_JOB_FAILURE';

/**
 * Create action object that is used to create a new job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {Object} job - The job object.
 * @param {string} job.job_type - What kind of job is it?
 * @param {string} job.job_visibility - Who is allowed to see this job.
 * @param {string} job.picture_cloudinary_public_id - Cloudinary public ID of a picture representing this job.
 * @param {string} job.title - A short title for this job.
 * @param {string} job.description - Description of this job.
 * @param {string} job.city - The city.
 * @param {string} job.country - Country Code.
 * @param {date} job.date_starts - The date at which this job will start. YYYY-MM-DD
 * @param {date} job.date_ends - The date at which this job will end. YYYY-MM-DD
 *
 * @return {Object}
 */
export const updateJob = (token, jobId, job) => ({
  [API]: {
    endpoint: `/job/${jobId}/update`,
    method: 'POST',
    body: job,
    token,
    types: [UPDATE_JOB_REQUEST, UPDATE_JOB_SUCCESS, UPDATE_JOB_FAILURE],
  },
});

export const GET_USER_JOBS_REQUEST = 'GET_USER_JOBS_REQUEST';
export const GET_USER_JOBS_SUCCESS = 'GET_USER_JOBS_SUCCESS';
export const GET_USER_JOBS_FAILURE = 'GET_USER_JOBS_FAILURE';

/**
 * Create action object that is used to list a users job.
 *
 * @param {string} token - JWT for request authentification.
 *
 * @return {Object}
 */
export const getUserJobs = token => ({
  [API]: {
    endpoint: '/user/jobs',
    method: 'GET',
    token,
    types: [
      GET_USER_JOBS_REQUEST,
      GET_USER_JOBS_SUCCESS,
      GET_USER_JOBS_FAILURE,
    ],
  },
});

export const GET_JOB_REQUEST = 'GET_JOB_REQUEST';
export const GET_JOB_SUCCESS = 'GET_JOB_SUCCESS';
export const GET_JOB_FAILURE = 'GET_JOB_FAILURE';

/**
 * Create action object that is used to get a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const getJob = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}`,
    method: 'GET',
    token,
    types: [GET_JOB_REQUEST, GET_JOB_SUCCESS, GET_JOB_FAILURE],
  },
});

export const PUBLISH_JOB_REQUEST = 'PUBLISH_JOB_REQUEST';
export const PUBLISH_JOB_SUCCESS = 'PUBLISH_JOB_SUCCESS';
export const PUBLISH_JOB_FAILURE = 'PUBLISH_JOB_FAILURE';

/**
 * Create action object that is used to publish a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const publishJob = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}/publish`,
    method: 'POST',
    token,
    types: [PUBLISH_JOB_REQUEST, PUBLISH_JOB_SUCCESS, PUBLISH_JOB_FAILURE],
  },
});

export const UNPUBLISH_JOB_REQUEST = 'UNPUBLISH_JOB_REQUEST';
export const UNPUBLISH_JOB_SUCCESS = 'UNPUBLISH_JOB_SUCCESS';
export const UNPUBLISH_JOB_FAILURE = 'UNPUBLISH_JOB_FAILURE';

/**
 * Create action object that is used to unpublish a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const unpublishJob = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}/unpublish`,
    method: 'POST',
    token,
    types: [
      UNPUBLISH_JOB_REQUEST,
      UNPUBLISH_JOB_SUCCESS,
      UNPUBLISH_JOB_FAILURE,
    ],
  },
});

export const DELETE_JOB_REQUEST = 'DELETE_JOB_REQUEST';
export const DELETE_JOB_SUCCESS = 'DELETE_JOB_SUCCESS';
export const DELETE_JOB_FAILURE = 'DELETE_JOB_FAILURE';

/**
 * Create action object that is used to delete a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const deleteJob = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}/cancel`,
    method: 'POST',
    token,
    types: [DELETE_JOB_REQUEST, DELETE_JOB_SUCCESS, DELETE_JOB_FAILURE],
  },
});

export const ADD_POSITION_REQUEST = 'ADD_POSITION_REQUEST';
export const ADD_POSITION_SUCCESS = 'ADD_POSITION_SUCCESS';
export const ADD_POSITION_FAILURE = 'ADD_POSITION_FAILURE';

/**
 * Create action object that is used create a new position and add it to a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {Object} position - The position object.
 * @param {string} position.skill - Talents skill.
 * @param {string} position.filters - A comma-separated list of search tags.
 *
 * @return {Object}
 */
export const addPosition = (token, jobId, position) => ({
  [API]: {
    endpoint: `/job/${jobId}/position/create`,
    method: 'POST',
    body: position,
    token,
    types: [ADD_POSITION_REQUEST, ADD_POSITION_SUCCESS, ADD_POSITION_FAILURE],
  },
});

export const UPDATE_POSITION_REQUEST = 'UPDATE_POSITION_REQUEST';
export const UPDATE_POSITION_SUCCESS = 'UPDATE_POSITION_SUCCESS';
export const UPDATE_POSITION_FAILURE = 'UPDATE_POSITION_FAILURE';

/**
 * Create action object that is used to update talent for a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {string} positionId - The talent/position ID.
 * @param {Object} position - The position object.
 * @param {string} position.skill - Talents skill.
 * @param {string} position.filters - A comma-separated list of search tags.
 *
 * @return {Object}
 */
export const updatePosition = (token, jobId, positionId, position) => ({
  [API]: {
    endpoint: `/job/${jobId}/position/${positionId}/update`,
    method: 'POST',
    body: position,
    token,
    types: [
      UPDATE_POSITION_REQUEST,
      UPDATE_POSITION_SUCCESS,
      UPDATE_POSITION_FAILURE,
    ],
  },
});

export const DELETE_POSITION_REQUEST = 'DELETE_POSITION_REQUEST';
export const DELETE_POSITION_SUCCESS = 'DELETE_POSITION_SUCCESS';
export const DELETE_POSITION_FAILURE = 'DELETE_POSITION_FAILURE';

/**
 * Create action object that is used to delete a position from a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {string} positionId - The talent/position ID.
 *
 * @return {Object}
 */
export const deletePosition = (token, jobId, positionId) => ({
  [API]: {
    endpoint: `/job/${jobId}/position/${positionId}/cancel`,
    method: 'POST',
    token,
    types: [
      DELETE_POSITION_REQUEST,
      DELETE_POSITION_SUCCESS,
      DELETE_POSITION_FAILURE,
    ],
  },
});

export const PUBLISH_POSITION_REQUEST = 'PUBLISH_POSITION_REQUEST';
export const PUBLISH_POSITION_SUCCESS = 'PUBLISH_POSITION_SUCCESS';
export const PUBLISH_POSITION_FAILURE = 'PUBLISH_POSITION_FAILURE';

/**
 * Create action object that is used to publish a position.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {string} positionId - The talent/position ID.
 * @param {Object} position - The position object.
 * @param {string} position.title - A custom title for this position.
 * @param {string} position.description - Tell more about the talent sought after.
 * @param {string} position.position_dates - Dates for this position, in free text form.
 *
 * @return {Object}
 */
export const publishPosition = (token, jobId, positionId, position) => ({
  [API]: {
    endpoint: `/job/${jobId}/position/${positionId}/publish`,
    method: 'POST',
    body: position,
    token,
    types: [
      PUBLISH_POSITION_REQUEST,
      PUBLISH_POSITION_SUCCESS,
      PUBLISH_POSITION_FAILURE,
    ],
  },
});

export const UNPUBLISH_POSITION_REQUEST = 'UNPUBLISH_POSITION_REQUEST';
export const UNPUBLISH_POSITION_SUCCESS = 'UNPUBLISH_POSITION_SUCCESS';
export const UNPUBLISH_POSITION_FAILURE = 'UNPUBLISH_POSITION_FAILURE';

/**
 * Create action object that is used to UNpublish a position.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {string} positionId - The talent/position ID.
 *
 * @return {Object}
 */
export const unpublishPosition = (token, jobId, positionId) => ({
  [API]: {
    endpoint: `/job/${jobId}/position/${positionId}/unpublish`,
    method: 'POST',
    token,
    types: [
      UNPUBLISH_POSITION_REQUEST,
      UNPUBLISH_POSITION_SUCCESS,
      UNPUBLISH_POSITION_FAILURE,
    ],
  },
});

export const SAVE_TALENT_REQUEST = 'SAVE_TALENT_REQUEST';
export const SAVE_TALENT_SUCCESS = 'SAVE_TALENT_SUCCESS';
export const SAVE_TALENT_FAILURE = 'SAVE_TALENT_FAILURE';

/**
 * Create action object that is used to save a talent into a job's shortlist
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobIdOrCode - Job ID or code.
 * @param {string} talentId - Talent ID of the talent to contact.
 *
 * @return {Object}
 */
export const saveTalent = (token, jobIdOrCode, talentId) => ({
  [API]: {
    endpoint: `/job/${jobIdOrCode}/shortlist/${talentId}/add`,
    method: 'POST',
    token,
    types: [SAVE_TALENT_REQUEST, SAVE_TALENT_SUCCESS, SAVE_TALENT_FAILURE],
  },
});

export const UNSAVE_TALENT = 'UNSAVE_TALENT';
export const UNSAVE_TALENT_REQUEST = 'UNSAVE_TALENT_REQUEST';
export const UNSAVE_TALENT_SUCCESS = 'UNSAVE_TALENT_SUCCESS';
export const UNSAVE_TALENT_FAILURE = 'UNSAVE_TALENT_FAILURE';

/**
 * Create action object that is used to remove a talent from a job's shortlist
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - Job ID.
 * @param {string} talentId - Talent ID of the talent to contact.
 *
 * @return {Object}
 */
export const unsaveTalent = (token, jobId, talentId) => ({
  [API]: {
    endpoint: `/job/${jobId}/shortlist/${talentId}/remove`,
    method: 'POST',
    token,
    types: [
      UNSAVE_TALENT_REQUEST,
      UNSAVE_TALENT_SUCCESS,
      UNSAVE_TALENT_FAILURE,
    ],
  },
});

export const REQUEST_TALENT = 'REQUEST_TALENT';
export const REQUEST_TALENT_REQUEST = 'REQUEST_TALENT_REQUEST';
export const REQUEST_TALENT_SUCCESS = 'REQUEST_TALENT_SUCCESS';
export const REQUEST_TALENT_FAILURE = 'REQUEST_TALENT_FAILURE';

/**
 * Create action object that is used to request a talent for a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobIdOrCode - Job ID or code.
 * @param {string} talentIdOrAll - Talent ID or the string all for all talents in a skill group.
 * @param {Object} request - The position object.
 * @param {string} request.job_description - Description.
 * @param {string} request.job_dates - Dates in plane text.
 * @param {string} request.compensation_amount - Amount.
 * @param {string} request.compensation_currency - Curreny.
 *
 * @return {Object}
 */
export const requestTalent = (token, jobIdOrCode, talentIdOrAll, request) => ({
  [API]: {
    endpoint: `/job/${jobIdOrCode}/request/${talentIdOrAll}`,
    method: 'POST',
    body: request,
    token,
    types: [
      REQUEST_TALENT_REQUEST,
      REQUEST_TALENT_SUCCESS,
      REQUEST_TALENT_FAILURE,
    ],
  },
});

export const APPLY_JOB_REQUEST = 'APPLY_JOB_REQUEST';
export const APPLY_JOB_SUCCESS = 'APPLY_JOB_SUCCESS';
export const APPLY_JOB_FAILURE = 'APPLY_JOB_FAILURE';

/**
 * Create action object that is used for TALENT to apply for a job.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const applyJob = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}/apply`,
    method: 'POST',
    token,
    types: [APPLY_JOB_REQUEST, APPLY_JOB_SUCCESS, APPLY_JOB_FAILURE],
  },
});

export const GET_MOODBOARD_REQUEST = 'GET_MOODBOARD_REQUEST';
export const GET_MOODBOARD_SUCCESS = 'GET_MOODBOARD_SUCCESS';
export const GET_MOODBOARD_FAILURE = 'GET_MOODBOARD_FAILURE';

/**
 * Create action object that is used to get a moodboard.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 *
 * @return {Object}
 */
export const getMoodboard = (token, jobId) => ({
  [API]: {
    endpoint: `/job/${jobId}/moodboard`,
    method: 'GET',
    token,
    types: [
      GET_MOODBOARD_REQUEST,
      GET_MOODBOARD_SUCCESS,
      GET_MOODBOARD_FAILURE,
    ],
  },
});

export const UPDATE_MOODBOARD_REQUEST = 'UPDATE_MOODBOARD_REQUEST';
export const UPDATE_MOODBOARD_SUCCESS = 'UPDATE_MOODBOARD_SUCCESS';
export const UPDATE_MOODBOARD_FAILURE = 'UPDATE_MOODBOARD_FAILURE';

/**
 * Create action object that is used to update a moodboard.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {Object} data - The post object.
 * @param {Array} data.talents - List of talents and pictures.
 *
 * @return {Object}
 */
export const updateMoodboard = (token, jobId, data) => ({
  [API]: {
    endpoint: `/job/${jobId}/moodboard/update`,
    method: 'POST',
    body: data,
    token,
    types: [
      UPDATE_MOODBOARD_REQUEST,
      UPDATE_MOODBOARD_SUCCESS,
      UPDATE_MOODBOARD_FAILURE,
    ],
  },
});

export const SEND_REQUESTS_REQUEST = 'SEND_REQUESTS_REQUEST';
export const SEND_REQUESTS_SUCCESS = 'SEND_REQUESTS_SUCCESS';
export const SEND_REQUESTS_FAILURE = 'SEND_REQUESTS_FAILURE';

/**
 * Create action object that is used to send one or more
 * option requests all at once.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} requestListId - The request list ID.
 * @param {Object} data - The post object.
 * @param {Array} data.requests - Multiple option requests, grouped in one api request.
 *
 * @return {Object}
 */
export const sendRequests = (token, requestListId, data) => ({
  [API]: {
    endpoint: `/job/${requestListId}/requests`,
    method: 'POST',
    body: data,
    token,
    types: [
      SEND_REQUESTS_REQUEST,
      SEND_REQUESTS_SUCCESS,
      SEND_REQUESTS_FAILURE,
    ],
  },
});
